import * as THREE from 'three'
import type { GSplatInfo } from '@/plugins/gsplat/lod/GSplatInfo'
import type { IGSplatResource } from '@/plugins/gsplat/types/ResourceTypes'
import { DEFAULT_MAX_PICK_RESULTS, DEFAULT_PICK_THRESHOLD } from '@/shared/constants'
import type { Ellipsoid } from './EllipsoidRaycast'
import { EllipsoidRaycast } from './EllipsoidRaycast'

/**
 * 高斯溅射拾取结果
 */
export interface GSplatPickResult {
  /** 被拾取的 GSplatInfo 对象 */
  splatInfo: GSplatInfo
  /** splat 在 GSplatInfo 中的索引 */
  splatIndex: number
  /** 从光线原点到交点的距离 */
  distance: number
  /** 世界空间交点位置 */
  point: THREE.Vector3
  /** 世界空间交点法线 */
  normal: THREE.Vector3
}

/**
 * 拾取选项
 */
export interface GSplatPickOptions {
  /** 最大返回结果数量，默认 100 */
  maxResults?: number
  /** 阈值系数，用于放宽碰撞检测范围，默认 1.0 */
  threshold?: number
  /** 最大检测距离，默认 Infinity */
  maxDistance?: number
  /** 是否使用 AABB 预检测加速，默认 true */
  useAABBCulling?: boolean
}

/**
 * 高斯溅射拾取器
 * 封装光线与高斯椭球体的相交检测逻辑
 *
 * @remarks
 * 核心流程：
 * 1. AABB 粗剔除（可选）
 * 2. 遍历每个 splat，构建椭球体
 * 3. 调用 EllipsoidRaycast.intersect() 进行精确检测
 * 4. 按距离排序，限制最大返回数量
 *
 * 性能优化：
 * - 使用静态临时变量避免内存分配
 * - AABB 预检测快速剔除不相交的对象
 * - 早期退出：当收集到足够多结果时停止
 */
export class GSplatPicker {
  // 可复用的临时变量
  private static _tempCenter = new THREE.Vector3()
  private static _tempScale = new THREE.Vector3()
  private static _tempRotation = new THREE.Quaternion()
  private static _tempBox = new THREE.Box3()
  private static _tempRaycaster = new THREE.Raycaster()

  /**
   * 拾取单个 GSplatInfo 中的 splats
   * @param ray - 世界空间光线
   * @param splatInfo - 要检测的 GSplatInfo
   * @param options - 拾取选项
   * @returns 按距离排序的拾取结果数组
   */
  static pickSplatInfo(
    ray: THREE.Ray,
    splatInfo: GSplatInfo,
    options?: GSplatPickOptions
  ): GSplatPickResult[] {
    // 解析选项
    const maxResults = options?.maxResults ?? DEFAULT_MAX_PICK_RESULTS
    const threshold = options?.threshold ?? DEFAULT_PICK_THRESHOLD
    const maxDistance = options?.maxDistance ?? Infinity
    const useAABBCulling = options?.useAABBCulling ?? true

    // AABB 粗剔除
    if (useAABBCulling) {
      const worldAabb = GSplatPicker._tempBox
      if (!splatInfo.getWorldAabb(worldAabb)) {
        return []
      }

      if (!ray.intersectsBox(worldAabb)) {
        return []
      }
    }

    // 检查资源类型，决定使用哪种拾取方案
    const resource = splatInfo.resource as IGSplatResource
    const centers = resource.centers

    // 如果有 centers 缓存，优先使用基于 centers 的简化拾取
    // 这对于 SOGS GPU 资源（GSplatSogsData）是唯一可行的方案
    if (centers && centers.length > 0) {
      return GSplatPicker.pickWithCenters(
        ray,
        splatInfo,
        centers,
        threshold,
        maxResults,
        maxDistance
      )
    }

    // 否则尝试使用完整的椭球体拾取
    return GSplatPicker.pickWithEllipsoids(ray, splatInfo, threshold, maxResults, maxDistance)
  }

  /**
   * 使用椭球体进行精确拾取（需要 CPU 端的 scale/rotation 数据）
   */
  private static pickWithEllipsoids(
    ray: THREE.Ray,
    splatInfo: GSplatInfo,
    threshold: number,
    maxResults: number,
    maxDistance: number
  ): GSplatPickResult[] {
    const results: GSplatPickResult[] = []
    const numSplats = splatInfo.numSplats

    // 遍历所有 splat
    for (let i = 0; i < numSplats; i++) {
      try {
        // 构建椭球体
        const center = splatInfo.getSplatWorldCenter(i, GSplatPicker._tempCenter)
        const scale = splatInfo.getSplatWorldScale(i, GSplatPicker._tempScale)
        const rotation = splatInfo.getSplatWorldRotation(i, GSplatPicker._tempRotation)

        const ellipsoid: Ellipsoid = {
          center: center.clone(),
          radii: scale.clone(),
          rotation: rotation.clone(),
        }

        // 椭球体相交检测
        const intersection = EllipsoidRaycast.intersect(ray, ellipsoid, threshold)

        if (intersection.intersects && intersection.distance <= maxDistance) {
          results.push({
            splatInfo,
            splatIndex: i,
            distance: intersection.distance,
            point: intersection.point,
            normal: intersection.normal,
          })
        }

        // 早期退出：如果已经收集到足够多的结果（2倍缓冲）
        if (results.length >= maxResults * 2) {
          break
        }
      } catch (_error) {}
    }

    // 按距离排序
    results.sort((a, b) => a.distance - b.distance)

    // 限制返回数量
    return results.slice(0, maxResults)
  }

  /**
   * 使用 centers 进行简化拾取（适用于 SOGS GPU 资源）
   *
   * 由于 SOGS GPU 资源没有 CPU 端的 scale/rotation 数据，
   * 我们使用简化的球体近似进行拾取：
   * - 将光线与每个 splat 中心点的小球体进行相交测试
   * - 球体半径根据 threshold 参数调整
   */
  private static pickWithCenters(
    ray: THREE.Ray,
    splatInfo: GSplatInfo,
    centers: Float32Array,
    threshold: number,
    maxResults: number,
    maxDistance: number
  ): GSplatPickResult[] {
    const results: GSplatPickResult[] = []
    const numSplats = Math.min(splatInfo.numSplats, Math.floor(centers.length / 3))
    const worldMatrix = splatInfo.node.matrixWorld

    // 使用固定球体半径进行近似（可根据场景调整）
    // threshold 参数用于调整灵敏度
    const sphereRadius = 0.02 * threshold

    const tempCenter = GSplatPicker._tempCenter
    const tempSphere = new THREE.Sphere()
    const tempIntersect = new THREE.Vector3()

    for (let i = 0; i < numSplats; i++) {
      // 获取本地空间中心
      tempCenter.set(centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2])

      // 变换到世界空间
      tempCenter.applyMatrix4(worldMatrix)

      // 创建球体
      tempSphere.set(tempCenter, sphereRadius)

      // 测试相交
      if (ray.intersectSphere(tempSphere, tempIntersect)) {
        const distance = ray.origin.distanceTo(tempIntersect)

        if (distance <= maxDistance) {
          results.push({
            splatInfo,
            splatIndex: i,
            distance,
            point: tempIntersect.clone(),
            normal: tempIntersect.clone().sub(tempCenter).normalize(),
          })
        }

        // 早期退出
        if (results.length >= maxResults * 2) {
          break
        }
      }
    }

    // 按距离排序
    results.sort((a, b) => a.distance - b.distance)

    // 限制返回数量
    return results.slice(0, maxResults)
  }

  /**
   * 批量拾取多个 GSplatInfo
   * @param ray - 世界空间光线
   * @param splatInfos - GSplatInfo 数组
   * @param options - 拾取选项
   * @returns 按距离排序的拾取结果数组
   */
  static pickMultiple(
    ray: THREE.Ray,
    splatInfos: GSplatInfo[],
    options?: GSplatPickOptions
  ): GSplatPickResult[] {
    const maxResults = options?.maxResults ?? DEFAULT_MAX_PICK_RESULTS
    const allResults: GSplatPickResult[] = []

    // 遍历所有 GSplatInfo
    for (const splatInfo of splatInfos) {
      const results = GSplatPicker.pickSplatInfo(ray, splatInfo, options)
      allResults.push(...results)

      // 早期退出
      if (allResults.length >= maxResults * 2) {
        break
      }
    }

    // 按距离排序
    allResults.sort((a, b) => a.distance - b.distance)

    // 限制返回数量
    return allResults.slice(0, maxResults)
  }

  /**
   * 从屏幕坐标拾取
   * @param screenX - 屏幕 X 坐标（像素）
   * @param screenY - 屏幕 Y 坐标（像素）
   * @param screenWidth - 屏幕宽度
   * @param screenHeight - 屏幕高度
   * @param camera - Three.js 相机
   * @param splatInfos - GSplatInfo 数组
   * @param options - 拾取选项
   * @returns 按距离排序的拾取结果数组
   */
  static pickFromScreen(
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number,
    camera: THREE.Camera,
    splatInfos: GSplatInfo[],
    options?: GSplatPickOptions
  ): GSplatPickResult[] {
    // 屏幕坐标转 NDC
    const ndcX = (screenX / screenWidth) * 2 - 1
    const ndcY = -(screenY / screenHeight) * 2 + 1

    return GSplatPicker.pickFromNDC(ndcX, ndcY, camera, splatInfos, options)
  }

  /**
   * 从归一化设备坐标拾取
   * @param ndcX - NDC X 坐标 (-1 到 1)
   * @param ndcY - NDC Y 坐标 (-1 到 1)
   * @param camera - Three.js 相机
   * @param splatInfos - GSplatInfo 数组
   * @param options - 拾取选项
   * @returns 按距离排序的拾取结果数组
   */
  static pickFromNDC(
    ndcX: number,
    ndcY: number,
    camera: THREE.Camera,
    splatInfos: GSplatInfo[],
    options?: GSplatPickOptions
  ): GSplatPickResult[] {
    // NDC 转光线
    const raycaster = GSplatPicker._tempRaycaster
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera)

    // 执行批量拾取
    return GSplatPicker.pickMultiple(raycaster.ray, splatInfos, options)
  }
}
